import React, { useState, useMemo } from 'react';

// Reusable drill-down modal:
// - full-screen overlay, dark backdrop
// - search box (case-insensitive, across every column value)
// - client-side pagination (50 rows/page by default)
// - graceful empty / loading / error states
// Props:
//   open, onClose      — visibility
//   title, subtitle    — modal header text
//   loading, error     — pass-through states
//   rows               — raw array of records
//   columns            — [{ key, label, render? }] — render(row) optional custom cell
//   pageSize           — default 50
export default function DetailModal({
  open, onClose,
  title, subtitle,
  loading = false, error = null,
  rows = [], columns = [],
  pageSize = 50,
}) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  // Reset paging + search whenever a new modal is opened
  React.useEffect(() => { if (open) { setPage(1); setQuery(''); } }, [open, title]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => columns.some(c => {
      const raw = c.render ? c.render(r) : r?.[c.key];
      return String(raw ?? '').toLowerCase().includes(q);
    }));
  }, [rows, columns, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);
  const showingFrom = filtered.length === 0 ? 0 : start + 1;
  const showingTo   = Math.min(start + pageSize, filtered.length);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white border border-slate-300 shadow-xl w-full max-w-6xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 bg-[#0f2e4d] text-white flex items-center justify-between">
          <div>
            {subtitle && <div className="text-[11px] uppercase tracking-wide text-blue-200">{subtitle}</div>}
            <div className="text-sm font-semibold">{title}</div>
          </div>
          <button className="text-white hover:bg-white/20 px-2 py-1" onClick={onClose}>✕</button>
        </div>

        <div className="p-4 overflow-auto flex-1 space-y-2">
          {loading && <p className="text-sm text-gray-500">Loading…</p>}
          {error && <p className="text-sm text-rose-700">Error: {error}</p>}
          {!loading && !error && rows.length === 0 && (
            <p className="text-sm text-gray-500">No detail records available.</p>
          )}

          {!loading && !error && rows.length > 0 && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  placeholder="Search across all fields..."
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                  className="flex-1 min-w-[200px] px-2 py-1 border border-slate-300 text-xs focus:outline-none focus:border-[#0f2e4d]"
                />
                <span className="text-[11px] text-slate-600">
                  {filtered.length === rows.length
                    ? `${filtered.length.toLocaleString('en-IN')} records`
                    : `${filtered.length.toLocaleString('en-IN')} of ${rows.length.toLocaleString('en-IN')} records`}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-slate-200">
                  <thead className="bg-slate-100 border-b border-slate-300 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wide text-slate-600 font-semibold w-10">#</th>
                      {columns.map(c => (
                        <th key={c.key} className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wide text-slate-600 font-semibold whitespace-nowrap">{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pageRows.map((r, i) => (
                      <tr key={start + i} className="hover:bg-slate-50">
                        <td className="px-2 py-1.5 font-mono text-slate-500">{start + i + 1}</td>
                        {columns.map(c => {
                          const v = c.render ? c.render(r) : r?.[c.key];
                          return (
                            <td key={c.key} className="px-2 py-1.5 text-slate-800 whitespace-nowrap">
                              {v == null || v === '' ? '—' : String(v)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {pageRows.length === 0 && (
                      <tr><td colSpan={columns.length + 1} className="px-4 py-6 text-center text-slate-500 text-xs">No records match this search.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between text-[11px] text-slate-600 flex-wrap gap-2 pt-1">
                <span>Showing {showingFrom}-{showingTo} of {filtered.length.toLocaleString('en-IN')}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(1)} disabled={currentPage <= 1} className="px-2 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-100">« First</button>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="px-2 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-100">‹ Prev</button>
                  <span className="px-2">Page {currentPage} of {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="px-2 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-100">Next ›</button>
                  <button onClick={() => setPage(totalPages)} disabled={currentPage >= totalPages} className="px-2 py-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-100">Last »</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
